import React, { useMemo } from "react";

import { useSelector } from "react-redux";
import { useSnapshot } from "valtio";

import LongArrowRightMD from "@busbud/design-system-components/dist/Icons/LongArrowRight/MD";
import Label from "@busbud/design-system-components/dist/Label";
import Stack from "@busbud/design-system-components/dist/Stack";
import { Button } from "@busbud/horizon";

import { patchSearchAdultWithSelectedSeats } from "@app/components/navigation-elements/navigation-wrapper-layout";
import { PassengerAndClassInformation } from "@app/components/navigation-elements/passenger-and-class-information";
import { PriceInformation } from "@app/components/navigation-elements/price-information";
import { useAppContext } from "@app/helpers/hooks";
import { useNavigation } from "@app/modules/results/hooks/use-navigation";
import { getSearch } from "@app/modules/results/selectors/state-accessors";
import { SelectedSeats } from "@app/modules/seat-selection-first/container/select-to-add-seat-legend";
import { useSeatSelectionFirst } from "@app/modules/seat-selection-first/hooks/use-seat-selection-first";
import {
  canAddMoreSeats,
  getCountPendingSeatsBySegmentId,
  getSegmentSelectedSeats,
  isFirstSegment,
  valtio_store
} from "@app/modules/seat-selection-first/store";
import { usePassengerStore } from "@app/modules/ticket-options/passenger-selector/passenger-store";
import type { TripOption, TripOptionsMatrix } from "@app/types/results-types";

import { SeatSelectionBottomSectionSkeleton } from "./seat-selection-bottom-section-skeleton";
import { useStyles } from "./seat-selection-bottom-section.styles";

interface Props {
  is_loading: boolean;
  is_external?: boolean;
  selected_trip: TripOption | null;
  segment_id: string;
  next_segment_id: string | undefined;
  trip_options_matrix: TripOptionsMatrix;
}

export const SeatSelectionBottomSection: React.FC<Props> = ({
  is_loading,
  is_external,
  selected_trip,
  segment_id,
  next_segment_id,
  trip_options_matrix
}) => {
  const { translator } = useAppContext();
  const search = useSelector(getSearch);
  const { selectSeats } = useNavigation();
  const { selected_seats } = useSnapshot(valtio_store);
  const { classes } = useStyles();
  const seats_for_segment = getSegmentSelectedSeats(segment_id);
  const { selected_seats_count } = usePassengerStore();
  const can_add_seats = canAddMoreSeats(segment_id);
  const is_first_segement = isFirstSegment(segment_id);
  const count_pending_seats = getCountPendingSeatsBySegmentId(segment_id);
  const { is_seat_selection_first_enabled } = useSeatSelectionFirst();
  // SSF override - 0 adults in search is actually the passengerless search
  // for which fallback is 1 adult.
  const _search = useMemo(() => {
    if (is_seat_selection_first_enabled) {
      return patchSearchAdultWithSelectedSeats(
        search,
        selected_seats_count || 1
      );
    }
    return search;
  }, [search, is_seat_selection_first_enabled, selected_seats_count]);

  if (is_loading || !selected_trip) {
    return <SeatSelectionBottomSectionSkeleton />;
  }

  return (
    <>
      <div className={classes.selectedSeats}>
        <SelectedSeats
          seats={seats_for_segment}
          price_in_cents={selected_trip.price_in_cents}
          class_name={selected_trip.class_name}
          show_add_seat_legend={!!is_first_segement && can_add_seats}
          count_pending_seats={count_pending_seats}
          show_max_passengers_alert={!!is_first_segement && !can_add_seats}
        />
      </div>
      <div className={classes.content}>
        <Stack className={classes.container} direction="column" spacing={2}>
          <Stack justifyContent="space-between" spacing={2}>
            <Stack direction="column" spacing={0.25}>
              <Label size="xl" fontWeight="bold">
                {translator.t("!checkout.summary.fees-taxes.total")}
              </Label>
              <PassengerAndClassInformation
                search={_search}
                selected_trip={selected_trip}
                trip_options_matrix={trip_options_matrix}
              />
            </Stack>
            <PriceInformation
              disabled={!selected_seats.length}
              search={_search}
              selected_trip={selected_trip}
              segment_id={segment_id}
            />
          </Stack>

          <Button
            data-cy="confirm-departure-selection"
            data-testid="confirm-departure-selection"
            isDisabled={
              !selected_trip.price_in_cents || !seats_for_segment.length
            }
            className="w-full"
            onClick={() => {
              selectSeats(
                selected_trip,
                is_external,
                selected_seats,
                next_segment_id
              );
            }}
            iconEnd={<LongArrowRightMD />}
            size="xl"
            appearance="primary"
          >
            {translator.t("!error.SelectedSeatsNotAvailable.button.continue")}
          </Button>
        </Stack>
      </div>
    </>
  );
};
